// frontend/src/askos/StudyPlanPage.js
//
// Full study plan view: all scheduled days/items for the student's active
// plan, with mark-complete checkboxes and a regenerate action.
import React, { useEffect, useState, useCallback } from 'react';
import { getStudyPlan, updateStudyPlanItem, generateStudyPlan } from '../api/studyPlanApi';

const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

const StudyPlanPage = ({ slug }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [regenerating, setRegenerating] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getStudyPlan(slug).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const toggleItem = async (dayIdx, item) => {
        const nextStatus = item.status === 'completed' ? 'pending' : 'completed';
        setData(d => ({
            ...d,
            days: d.days.map((day, i) => i !== dayIdx ? day : {
                ...day,
                items: day.items.map(it => it.id === item.id ? { ...it, status: nextStatus } : it),
            }),
        }));
        try {
            await updateStudyPlanItem(slug, item.id, { status: nextStatus });
        } catch {
            load();
        }
    };

    const handleRegenerate = async () => {
        setRegenerating(true);
        try {
            await generateStudyPlan(slug);
            load();
        } finally {
            setRegenerating(false);
        }
    };

    if (loading) return <div className="subpage"><p className="empty-note">Loading…</p></div>;

    return (
        <div className="subpage">
            <div className="panel-heading" style={{ marginBottom: 20 }}>
                <h2>Study Plan</h2>
                <button type="button" className="secondary-btn" onClick={handleRegenerate} disabled={regenerating}>
                    {regenerating ? 'Regenerating…' : '↻ Regenerate Plan'}
                </button>
            </div>

            {!data?.has_active_plan && (
                <p className="empty-note">No active study plan yet. Generate one from the dashboard's Study Plan card.</p>
            )}

            {data?.has_active_plan && data.days.length === 0 && (
                <p className="empty-note">Your plan has no scheduled items.</p>
            )}

            {data?.has_active_plan && data.days.map((day, dayIdx) => (
                <div key={day.date} style={{ marginBottom: 24 }}>
                    <h4 style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>{fmtDate(day.date)}</h4>
                    <div className="panel">
                        {day.items.map(item => (
                            <div key={item.id} className="study-row">
                                <div className={`status${item.status === 'completed' ? ' done' : ''}`} onClick={() => toggleItem(dayIdx, item)} style={{ cursor: 'pointer' }}>
                                    {item.status === 'completed' && '✓'}
                                </div>
                                <div className="study-info">
                                    <strong>{item.title}</strong>
                                    {item.source_label && <span>{item.source_label}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default StudyPlanPage;
